import * as d3 from 'd3';
import React, { useRef, useEffect } from 'react';

// TODO: Trigger transition on scrolled to section
// TODO: Add hover tooltips

const DonutChart = ({ dataArray, colorsArray }) => {
  const svgRef = useRef(null);

  const generateDonutChart = (
    data,
    {
      name = ([x]) => x, // given d in data, returns the (ordinal) label
      value = ([, y]) => y, // given d in data, returns the (quantitative) value
      title, // given d in data, returns the title text
      width = 640, // outer width, in pixels
      height = 400, // outer height, in pixels
      innerRadius = Math.min(width, height) / 4, // inner radius of pie, in pixels (non-zero for donut)
      outerRadius = Math.min(width, height) / 2, // outer radius of pie, in pixels
      labelRadius = (innerRadius + outerRadius) / 2, // center radius of labels
      format = ',', // a format specifier for values (in the label)
      names, // array of names (the domain of the color scale)
      colors, // array of colors for names
      stroke = innerRadius > 0 ? 'none' : 'white', // stroke separating widths
      strokeWidth = 4, // width of stroke separating wedges
      padAngle = stroke === 'none' ? 1 / outerRadius : 0, // angular separation between wedges
    } = {}
  ) => {
    // Compute values.
    const N = d3.map(data, name);
    const V = d3.map(data, value);
    const I = d3.range(N.length).filter((i) => !isNaN(V[i]));

    // Unique the names.
    if (names === undefined) names = N;
    names = new d3.InternSet(names);

    // Chose a default color scheme based on cardinality.
    if (colors === undefined) {
      colors = d3.schemeSpectral[names.size];
    }
    if (colors === undefined) {
      colors = d3.quantize(
        (t) => d3.interpolateSpectral(t * 0.8 + 0.1),
        names.size
      );
    }

    // Construct scales.
    const color = d3.scaleOrdinal(names, colors);

    // Compute titles.
    if (title === undefined) {
      const formatValue = d3.format(format);
      title = (i) => `${N[i]}\n${formatValue(V[i])}`;
    } else {
      const O = d3.map(data, (d) => d);
      const T = title;
      title = (i) => T(O[i], i, data);
    }

    // Construct arcs.
    const arcs = d3
      .pie()
      .padAngle(padAngle)
      .sort(null)
      .value((i) => V[i])(I);
    const arc = d3.arc().innerRadius(innerRadius).outerRadius(outerRadius);
    const arcLabel = d3.arc().innerRadius(labelRadius).outerRadius(labelRadius);

    d3.select(svgRef.current)
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', [-width / 2, -height / 2, width, height])
      .attr('style', 'max-width: 100%; height: auto; height: intrinsic;')
      .append('g')
      .attr('stroke', stroke)
      .attr('stroke-width', strokeWidth)
      .attr('stroke-linejoin', 'round')
      .selectAll('path')
      .data(arcs)
      .join('path')
      .attr('fill', (d) => color(N[d.data]))
      .attr('d', arc)
      .append('title')
      .text((d) => title(d.data));

    const path = d3.select(svgRef.current).selectAll('path');
    path
      .transition()
      .duration(480)
      .ease(d3.easeLinear)
      .attrTween('d', (d) => {
        const i = d3.interpolate(d.startAngle + 0.1, d.endAngle);
        return function (t) {
          d.endAngle = i(t);
          return arc(d);
        };
      });

    d3.select(svgRef.current)
      .append('g')
      .attr('font-family', 'sans-serif')
      .attr('font-size', 16)
      .attr('text-anchor', 'middle')
      .selectAll('text')
      .data(arcs)
      .join('text')
      .attr('transform', (d) => `translate(${arcLabel.centroid(d)})`)
      .selectAll('tspan')
      .data((d) => {
        const lines = `${title(d.data)}`.split(/\n/);
        return d.endAngle - d.startAngle > 0.25 ? lines : lines.slice(0, 1);
      })
      .join('tspan')
      .attr('x', 0)
      .attr('y', (_, i) => `${i * 1.1}em`)
      .attr('font-weight', (_, i) => (i ? null : 'bold'))
      .attr('style', 'fill:#fff')
      .text((d) => d);
  };

  useEffect(() => {
    generateDonutChart(dataArray, {
      name: (d) => d.name,
      value: (d) => d.value,
      width: 400,
      height: 250,
      names: dataArray.map((d) => d.name),
      colors: colorsArray,
      stroke: 'none',
      title: (d) => `${d.value}%`,
    });
  }, [dataArray, colorsArray]);

  return <svg className="donut-chart" ref={svgRef}></svg>;
};

export default DonutChart;
